// File Location: src/components/common/GlobalSearchBar.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  X, 
  FileText, 
  Calendar, 
  MapPin, 
  User, 
  AlertCircle, 
  Loader,
  Clock,
  Building,
  Briefcase,
  Sparkles,
  History,
  Frown,
  Stethoscope
} from 'lucide-react';
import { searchApplicationsUniversal } from '../../services/universalSearchService';

const GlobalSearchBar = ({ userRole = null, className = '' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const inputRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('global_search_recent_searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading recent searches:', e);
      }
    }
  }, []);

  // Handle Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setShowResults(true);
      }
      if (e.key === 'Escape' && showResults) {
        setShowResults(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showResults]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    if (showResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showResults]);

  // Debounce search
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setResults([]);
      setMessage('');
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await searchApplicationsUniversal(searchTerm, userRole);
        
        if (response.success) {
          setResults(response.results);
          setMessage(response.message);
          setShowResults(true);
          
          // Save to recent searches (max 5)
          if (response.results.length > 0) {
            const recent = [searchTerm, ...recentSearches.filter(s => s !== searchTerm)].slice(0, 5);
            setRecentSearches(recent);
            localStorage.setItem('global_search_recent_searches', JSON.stringify(recent));
          }
        } else {
          setResults([]);
          setMessage(response.error || 'Search failed');
          setShowResults(true);
        }
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
        setMessage('Failed to search applications');
        setShowResults(true);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, userRole]);

  const handleSelectApplication = (e, application) => {
    const appId = application.applicationId;
    
    if (!appId) {
      console.error('No applicationId found in application:', application);
      return;
    }
    
    let url = '';
    if (userRole === 'hospital') {
      url = `/hospital/application/${appId}`;
    } else if (userRole === 'policy') {
      url = `/policy/details/${appId}`;
    } else if (userRole === 'doctor') {
      url = `/doctor/application-review/${appId}`;
    } else if (userRole === 'finance') {
      url = `/dashboard/financial/review/${appId}`;
    } else if (userRole === 'admin') {
      url = `/admin/hospital-details/${appId}`;
    } else {
      url = `/hospital/application/${appId}`;
    }
    
    // Open in new tab (always for global search)
    const fullUrl = `${window.location.origin}${url}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
    
    setShowResults(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
    setMessage('');
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (recentSearches.length > 0 && searchTerm.length === 0) {
      setShowResults(true);
    }
  };

  const handleBlur = () => {
    // Delay to allow click events to register
    setTimeout(() => {
      setIsFocused(false);
    }, 200);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'submitted': 'bg-blue-100 text-blue-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'reviewing': 'bg-purple-100 text-purple-800',
      'disputed': 'bg-orange-100 text-orange-800'
    };

    const statusClass = statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass}`}>
        {status || 'Unknown'}
      </span>
    );
  };

  const getCategoryLabel = (result) => {
    // Determine category based on application data
    if (result.emergency) return { label: 'Emergency', color: 'bg-red-500', icon: AlertCircle };
    if (result.priority === 'urgent') return { label: 'Urgent', color: 'bg-orange-500', icon: Clock };
    if (result.priority === 'high') return { label: 'High Priority', color: 'bg-yellow-500', icon: AlertCircle };
    return { label: 'Application', color: 'bg-blue-500', icon: FileText };
  };

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      <div className="relative">
        {/* Glow effect on focus */}
        <div className={`absolute inset-0 bg-teal-500/20 rounded-lg blur-md transition-all duration-300 ${isFocused && searchTerm.length >= 2 ? 'opacity-100 animate-pulse' : 'opacity-0'}`}></div>
        
        <div className={`relative flex items-center transition-all duration-300 ${isFocused ? 'ring-2 ring-teal-400 ring-offset-2 rounded-lg' : ''}`}>
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <Search className={`h-5 w-5 transition-colors ${isFocused ? 'text-teal-600' : 'text-gray-400'}`} />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="🔍 Search by patient name, ID, or policy… (Ctrl+K)"
            className={`block w-full pl-10 pr-28 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 text-sm transition-all ${
              isFocused ? 'shadow-lg bg-gradient-to-r from-teal-50 to-white' : 'shadow-sm'
            }`}
          />
          
          {/* Keyboard shortcut hint */}
          <div className="absolute inset-y-0 right-0 pr-10 flex items-center pointer-events-none">
            <span className="text-xs text-gray-400 hidden sm:inline-flex items-center gap-1 border border-gray-300 rounded px-1.5 py-0.5">
              <span className="text-[10px]">Ctrl</span>
              <span className="text-[10px]">+</span>
              <span className="text-[10px]">K</span>
            </span>
          </div>

          {searchTerm && (
            <button
              onClick={handleClear}
              className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-700 transition-colors"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          )}
          
          {loading && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Loader className="h-4 w-4 text-teal-500 animate-spin" />
            </div>
          )}
        </div>
      </div>

      {/* Results Dropdown */}
      {showResults && (
        <div 
          className="absolute z-50 mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto animate-in fade-in slide-in-from-top-2"
          ref={resultsRef}
        >
          {results.length > 0 ? (
            <div className="p-2">
              {searchTerm && (
                <div className="text-xs text-gray-500 mb-2 px-2 flex items-center gap-2">
                  <Search className="h-3 w-3" />
                  {message}
                </div>
              )}
              
              {results.map((result) => {
                const category = getCategoryLabel(result);
                const Icon = category.icon;
                
                return (
                  <div
                    key={result.id}
                    onClick={(e) => handleSelectApplication(e, result)}
                    className="p-3 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`h-4 w-4 text-${category.color.replace('bg-', '')}-600`} />
                          <span className="font-semibold text-gray-900">
                            {result.applicationId}
                          </span>
                          {getStatusBadge(result.status)}
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <User className="h-3 w-3" />
                          <span className="font-medium">{result.patientName}</span>
                        </div>

                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate max-w-xs">{result.hospitalName}</span>
                          </div>
                          {result.policyNumber && result.policyNumber !== 'N/A' && (
                            <div className="flex items-center gap-1">
                              <Briefcase className="h-3 w-3" />
                              <span>Policy: {result.policyNumber}</span>
                            </div>
                          )}
                          {result.submittedAt && (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>
                                {result.submittedAt?.toDate ? 
                                  new Date(result.submittedAt.toDate()).toLocaleDateString() : 
                                  'N/A'}
                              </span>
                            </div>
                          )}
                        </div>

                        {result.diagnosis && result.diagnosis !== 'N/A' && result.diagnosis && (
                          <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                            <Stethoscope className="h-3 w-3" />
                            <span className="line-clamp-1">{result.diagnosis}</span>
                          </div>
                        )}
                      </div>

                      {result.emergency && (
                        <div className="ml-2">
                          <AlertCircle className="h-5 w-5 text-red-500" />
                        </div>
                      )}
                    </div>
                    
                    {/* Category label */}
                    <div className="flex items-center gap-1 mt-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white ${category.color}`}>
                        {category.label}
                      </span>
                      {result.priority && result.priority !== 'normal' && (
                        <span className="text-xs text-gray-500">
                          • {result.priority.charAt(0).toUpperCase() + result.priority.slice(1)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : !loading && searchTerm.length >= 2 ? (
            <div className="p-8 text-center">
              <div className="flex flex-col items-center justify-center">
                <div className="relative">
                  <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <Frown className="h-6 w-6 text-gray-400 absolute top-1 right-2" />
                </div>
                <p className="text-sm text-gray-600 font-medium mb-1">No results found</p>
                <p className="text-xs text-gray-500">Try a different search term or check your spelling</p>
              </div>
            </div>
          ) : searchTerm.length === 0 && recentSearches.length > 0 ? (
            <div className="p-2">
              <div className="text-xs text-gray-500 mb-2 px-2 flex items-center gap-2">
                <History className="h-3 w-3" />
                Recent searches
              </div>
              {recentSearches.slice(0, 3).map((search, idx) => (
                <button
                  key={idx}
                  onClick={() => setSearchTerm(search)}
                  className="w-full text-left p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors flex items-center gap-2 text-sm text-gray-600"
                >
                  <Clock className="h-4 w-4 text-gray-400" />
                  {search}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;

